<template>
  <div
    class="summary-card"
    :class="[borderClass, { selected, compact }]"
    @click="emit('click')"
  >
    <div class="summary-card-header">
      <div class="summary-card-title-row">
        <DpplSvg
          class="summary-card-icon"
          :class="iconClass"
          :file-name="icon"
        />
        <span class="text-h3 text-dark summary-card-label">{{ label }}</span>
      </div>
      <InsightsTooltip
        v-if="tooltip?.key"
        :tooltip="tooltip"
        class="summary-tooltip"
        @click.stop
      />
    </div>
    <p class="summary-card-value text-darkest">
      {{ numberWithCommas(value) }}
    </p>
    <p class="text-body-1 text-dark summary-card-caption">
      {{ caption }}
    </p>
  </div>
</template>

<script setup>
import { numberWithCommas } from '@/helpers/formatters';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';

defineProps({
  icon: { type: String, required: true },
  iconClass: { type: String, default: '' },
  borderClass: { type: String, default: '' },
  label: { type: String, required: true },
  value: { type: Number, default: 0 },
  tooltip: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  caption: { type: String, default: 'employees' },
});

const emit = defineEmits(['click']);
</script>

<style lang="less" scoped>
.summary-card {
  position: relative;
  min-height: 136px;
  height: 100%;
  padding: 16px;
  border-radius: 6px;
  border: 1px solid;
  background: @white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  cursor: pointer;

  &.border-no-acknowledgement {
    border-color: @red-700;

    &.selected {
      background: @red-100;
    }

    &:hover {
      background: fade(@red-100, 50%);
    }
  }

  &.border-no-survey {
    border-color: @red-500;

    &.selected {
      background: @red-100;
    }

    &:hover {
      background: fade(@red-100, 50%);
    }
  }

  &.border-pending {
    border-color: @grey-800;

    &.selected {
      background: @grey-100;
    }

    &:hover {
      background: fade(@grey-100, 50%);
    }
  }

  &.border-incomplete {
    border-color: @orange-700;

    &.selected {
      background: @orange-100;
    }

    &:hover {
      background: fade(@orange-100, 50%);
    }
  }

  &.border-out-of-date-survey {
    border-color: @purple-500;

    &.selected {
      background: @purple-100;
    }

    &:hover {
      background: fade(@purple-100, 50%);
    }
  }

  &.border-job-profile-changed {
    border-color: @navy-700;

    &.selected {
      background: @navy-100;
    }

    &:hover {
      background: fade(@navy-100, 50%);
    }
  }

  &.border-mismatched {
    border-color: @pink-500;

    &.selected {
      background: @pink-100;
    }

    &:hover {
      background: fade(@pink-100, 50%);
    }
  }

  &.border-out-of-date {
    border-color: @yellow-700;

    &.selected {
      background: @yellow-100;
    }

    &:hover {
      background: fade(@yellow-100, 50%);
    }
  }

  &.border-out-of-sla {
    border-color: @yellow-700;

    &.selected {
      background: @yellow-100;
    }

    &:hover {
      background: fade(@yellow-100, 50%);
    }
  }

  &.compact {
    .summary-tooltip {
      display: none;
    }
  }

}

.summary-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  width: 100%;
  min-height: 28px;
}

.summary-card-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.summary-card-label {
  flex: 1;
  line-height: 120%;
}

.summary-card-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;

  :deep(path) {
    fill: currentColor;
  }

  &.icon-no-acknowledgement {
    color: @red-700;

    & + .summary-card-label {
      color: @red-700;
    }
  }

  &.icon-no-survey {
    color: @red-500;

    & + .summary-card-label {
      color: @red-500;
    }
  }

  &.icon-pending {
    color: @grey-800;
  }

  &.icon-incomplete {
    color: @orange-700;

    & + .summary-card-label {
      color: @orange-700;
    }
  }

  &.icon-out-of-date-survey {
    color: @purple-500;

    & + .summary-card-label {
      color: @purple-500;
    }
  }

  &.icon-job-profile-changed {
    color: @navy-700;

    & + .summary-card-label {
      color: @navy-700;
    }
  }

  &.icon-mismatched {
    color: @pink-500;

    & + .summary-card-label {
      color: @pink-500;
    }
  }

  &.icon-out-of-date {
    color: @yellow-700;

    & + .summary-card-label {
      color: @yellow-700;
    }
  }

  &.icon-out-of-sla {
    color: @yellow-700;

    & + .summary-card-label {
      color: @yellow-700;
    }
  }
}

.summary-tooltip {
  flex-shrink: 0;
}

.summary-card-value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 4px;
}

.summary-card-caption {
  font-weight: 400;
}
</style>
